"use client"

import useIsHydrated from "@/reusable/hooks/useIsHydrated"
import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import { extractBlockProperties } from "@/utils/simplifyData"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { useInView } from "react-intersection-observer"
import { transitionDelayTime, useInViewConfig, useTransitionUpStyles } from "ui"
import { mediaQueries } from "ui/theme/mediaQueries"
import { colors, sizes } from "ui/theme/theme"

const FooterContainer = styled(Box)`
  width: 100%;
  border-top: 1px solid ${colors.black};
  padding-top: ${sizes.baseSingle};
  padding-bottom: ${sizes.baseSingle};
  margin-top: ${sizes.baseDouble};
  display: flex;
  flex-direction: column;
  color: ${colors.black};

  ${mediaQueries.desktopM} {
    flex-direction: row;
    gap: 80px;
    padding-top: 40px;
    padding-bottom: 60px;
  }
`

const PhoneTitle = styled(Typography)<{ component?: React.ElementType }>`
  font-size: 31px;
  font-weight: 700;
  margin-bottom: 40px;
  flex-grow: 1;
  color: inherit;
  ${mediaQueries.tabletDown} {
    font-size: 31px;
    margin-top: 40px;
  }
  ${mediaQueries.desktopM} {
    margin-bottom: ${sizes.baseSingle};
    font-size: 50px;
    white-space: nowrap;
  }
`

const PhoneItemTitle = styled(Typography)<{ component?: React.ElementType }>`
  font-size: 16px;
  margin-bottom: ${sizes.baseSingle};
  color: ${colors.gray};
  ${mediaQueries.desktopM} {
    font-size: 18px;
  }
`

const PhoneItemNumber = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  color: inherit;
  margin-bottom: 4px;

  a {
    color: ${colors.black};
    text-decoration: none;
  }
  p {
    margin: 0;
  }

  ${mediaQueries.desktopM} {
    font-size: 16px;
  }
`

const StyledGrid = styled(Box)`
  width: 100%;
  display: grid;

  grid-template-columns: repeat(2, 1fr);

  ${mediaQueries.desktopM} {
    grid-template-columns: repeat(3, 1fr);
    column-gap: 60px;
    row-gap: 12px;
  }
`

interface PhoneItem {
  title: string
  phoneNumber: string
}

interface CommunityFooterProps {
  data: any
}

const CommunityFooter = ({ data }: CommunityFooterProps) => {
  const { phoneList, bottomTitle } = data
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView, transitionDelayTime)
  const { isHydrated } = useIsHydrated()

  // If phoneList doesn't exist or doesn't have blocks, don't render anything
  if (!phoneList?.blocks || phoneList.blocks.length === 0) {
    return null
  }

  // Extract the phone items from the blocks
  const phoneItems: PhoneItem[] = extractBlockProperties(phoneList.blocks)

  return (
    <FooterContainer ref={ref} style={transitionStyles}>
      {bottomTitle?.value && (
        <PhoneTitle component="h2">{bottomTitle.value}</PhoneTitle>
      )}
      <StyledGrid>
        {isHydrated &&
          phoneItems.map((item, index) => (
            <Box key={`phone-item-${index}`}>
              <PhoneItemTitle component="h3">{item.title}</PhoneItemTitle>
              <PhoneItemNumber
                suppressHydrationWarning
                dangerouslySetInnerHTML={{
                  __html: replaceRichTextLinks(item.phoneNumber),
                }}
              ></PhoneItemNumber>
            </Box>
          ))}
      </StyledGrid>
    </FooterContainer>
  )
}

export default CommunityFooter
